import _styled, { css as _css, default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8 } from "styled-components";
import React from "react";
import { Link } from "~/features/ui/link";
import type { NavDropdownProduct } from "~/features/app/app-header-v2/nav-links/nav-dropdown-types";
import { Asset } from "~/features/craft/asset";
import { takeFirst } from "~/features/app/utils";
import { cleanStegaForTextTransform } from "~/features/sanity/stega";
type Props = {
  product: NavDropdownProduct;
  isActive?: boolean;
  onNavigate: () => void;
  surface?: "dark" | "light";
};
function extractTitleAndDescription(rawText: string): {
  title: string;
  description: string | null;
} {
  // Cleaned because the output is split into per-line spans by GSAP SplitText, which fractures the
  // appended stega payload (it contains a zero-width space) into malformed fragments.
  const text = cleanStegaForTextTransform(rawText);
  const match = text.match(/^(.*?)\s*\[(.*?)\]\s*$/);
  if (match && match[1] !== undefined && match[2] !== undefined) {
    return {
      title: match[1].trim(),
      description: match[2].trim()
    };
  }
  return {
    title: text,
    description: null
  };
}
export function ProductLink({
  product,
  isActive,
  onNavigate,
  surface = "dark"
}: Props) {
  const {
    title,
    description
  } = extractTitleAndDescription(product.title);
  const isLight = surface === "light";
  return <_StyledLink data-product-text href={product.url} onClick={onNavigate} $_css2={[{
    "display": "flex",
    "height": "8rem",
    "alignItems": "center",
    "justifyContent": "space-between",
    "gap": "1.6rem",
    "paddingLeft": "2.8rem",
    "paddingRight": "2.8rem",
    "@media (min-width: 1024px)": {
      "backgroundColor": "transparent",
      "paddingLeft": "1.6rem",
      "paddingRight": "1.6rem"
    }
  }, isLight ? {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(239 239 246 / var(--tw-bg-opacity))",
    "--tw-text-opacity": "1",
    "color": "rgb(0 7 4 / var(--tw-text-opacity))"
  } : {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(23 23 23 / var(--tw-bg-opacity))",
    "--tw-text-opacity": "1",
    "color": "rgb(255 255 255 / var(--tw-text-opacity))"
  }]}>
      {product.image && <_StyledDiv data-product-image $_css3={[{
      "position": "relative",
      "display": "flex",
      "height": "4.8rem",
      "minHeight": "4.8rem",
      "width": "4.8rem",
      "minWidth": "4.8rem",
      "alignItems": "center",
      "justifyContent": "center",
      "overflow": "hidden",
      "borderRadius": "4px"
    }, isLight ? {
      "backgroundColor": "rgb(0 7 4 / 0.1)"
    } : {
      "backgroundColor": "rgb(255 255 255 / 0.1)"
    }]}>
          <Asset asset={takeFirst(product.image)} imageProps={{
        fit: "cover",
        width: 48,
        ratio: "1/1"
      }} />
        </_StyledDiv>}
      <_StyledDiv2>
        <_StyledDiv3>
          <_StyledH data-split-text $_css4={[{
          "fontSize": "1.6rem",
          "lineHeight": "1.1",
          "letterSpacing": "0.016rem",
          "fontWeight": "400"
        }, isLight ? {
          "--tw-text-opacity": "1",
          "color": "rgb(0 7 4 / var(--tw-text-opacity))"
        } : {
          "color": "inherit"
        }]}>
            {title}
          </_StyledH>
        </_StyledDiv3>
        {description && <_StyledDiv4>
            <_StyledP data-split-text $_css5={[{
          "overflow": "hidden",
          "textOverflow": "ellipsis",
          "whiteSpace": "nowrap",
          "fontSize": "1.4rem",
          "lineHeight": "1.5",
          "letterSpacing": "0.014000000000000002rem",
          "fontWeight": "400"
        }, isLight ? {
          "--tw-text-opacity": "1",
          "color": "rgb(0 7 4 / var(--tw-text-opacity))",
          "opacity": "0.5"
        } : {
          "--tw-text-opacity": "1",
          "color": "rgb(255 255 255 / var(--tw-text-opacity))",
          "opacity": "0.5"
        }]}>
              {description}
            </_StyledP>
          </_StyledDiv4>}
      </_StyledDiv2>
      {/* Blue square that scales in with the line */}
      <_StyledDiv5 className="blue-square" $_css6={[{
      "marginLeft": "1.6rem",
      "marginTop": "0.6rem",
      "--tw-bg-opacity": "1",
      "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
      "willChange": "transform"
    }, _css(["width:8px;height:8px;transform:scale(0);transition:transform 0.3s cubic-bezier(0.125,0.425,0.27,1);"]), isActive && _css(["transform:scale(1);"])]} />
    </_StyledLink>;
}
var _StyledLink = /*#__PURE__*/_styled(Link).withConfig({
  componentId: "sc-sf-1i13o2n-0"
})`${p => p.$_css2}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-1i13o2n-1"
})`${p => p.$_css3}`;
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-1i13o2n-2"
})({
  "flex": "1 1 0%"
});
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-1i13o2n-3"
})({
  "overflow": "hidden"
});
var _StyledH = _styled5("h4").withConfig({
  componentId: "sc-sf-1i13o2n-4"
})`${p => p.$_css4}`;
var _StyledDiv4 = _styled6("div").withConfig({
  componentId: "sc-sf-1i13o2n-5"
})({
  "overflow": "hidden"
});
var _StyledP = _styled7("p").withConfig({
  componentId: "sc-sf-1i13o2n-6"
})`${p => p.$_css5}`;
var _StyledDiv5 = /*#__PURE__*/_styled8("div").withConfig({
  displayName: "product-link___StyledDiv5",
  componentId: "sc-61iip6-0"
})(["", ""], p => p.$_css6);